import { useState, useEffect, useCallback } from "react";

type ViewMode = "grid" | "list";

interface ViewPreferenceConfig {
  entityType: string;
  defaultView?: ViewMode;
}

/**
 * useViewPreference - Hook for persisting user's preferred view mode per entity type
 * 
 * Features:
 * - Persists preference in localStorage
 * - Separate preference per entity type (projects, customers, etc.)
 * - Falls back to provided default if no preference stored
 * - Auto-switches to grid/card view on mobile (< 640px) to prevent horizontal scroll
 */
export function useViewPreference({ entityType, defaultView = "list" }: ViewPreferenceConfig) {
  const storageKey = `doyence-view-preference-${entityType}`;
  
  const [storedView, setStoredViewState] = useState<ViewMode>(() => {
    if (typeof window === "undefined") return defaultView;
    
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === "grid" || stored === "list") {
        return stored;
      }
    } catch (e) {
      console.warn("Failed to read view preference from localStorage:", e);
    }
    return defaultView;
  });

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 640;
  });

  // Track viewport width
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Persist to localStorage when storedView changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, storedView);
    } catch (e) {
      console.warn("Failed to save view preference to localStorage:", e);
    }
  }, [storageKey, storedView]);

  const setViewMode = useCallback((mode: ViewMode) => {
    setStoredViewState(mode);
  }, []);

  const toggleViewMode = useCallback(() => {
    setStoredViewState(prev => prev === "grid" ? "list" : "grid");
  }, []);

  // On mobile, force grid (card) view; on desktop, use stored preference
  const viewMode = isMobile ? "grid" : storedView;

  return {
    viewMode,
    setViewMode,
    toggleViewMode,
    isGridView: viewMode === "grid",
    isListView: viewMode === "list",
  };
}

export type { ViewMode };
